import { useEffect } from "react";
import { FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { CouponsFormValues } from "src/models/Coupons.model";
import ATMDatePicker from "src/components/UI/atoms/formFields/ATMDatePicker/ATMDatePicker";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import ATMMultiSelect from "src/components/UI/atoms/formFields/ATMMultiSelect/ATMMultiSelect";
import moment from "moment";
import { handleValidDecimalNumber } from "src/utils/formUtils/ValidNumber";
import useUser from "src/hooks/useUser";
import useProduct from "src/hooks/useProduct";

type Props = {
  formikProps: FormikProps<CouponsFormValues>;
  onClose: () => void;
  formType: "ADD" | "EDIT";
};

const CouponForm = ({ formikProps, onClose, formType }: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;
  const { user, isDataLoading: isUserLoading } = useUser();
  const { product, isDataLoading: isProductLoading } = useProduct();

  const studentsOption = user?.map((u) => ({
    label: u.firstName + " " + u.lastName,
    value: u._id,
  }));

  const productsOption = product?.map((p) => ({
    label: p.productName,
    value: p._id,
  }));

  const discountOption = [
    { label: "Flat", value: "FLAT" },
    { label: "Percentage", value: "PERCENTAGE" },
  ];

  // Generate random coupon code
  const generateCouponCode = (): string => {
    return Math.random().toString(36).substring(2, 8).toUpperCase();
  };

  // ✅ Run only once when component mounts (for ADD mode)
  useEffect(() => {
    if (formType === "ADD" && !values.couponCode) {
      setFieldValue("couponCode", generateCouponCode());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-4 flex flex-col gap-6">
      <div className="flex justify-between items-center">
        <div className="text-xl font-medium">
          {formType === "ADD" ? "Add Coupon" : "Update Coupon"}
        </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>

      <div className="flex flex-col gap-4">
        {/* Coupon Code */}
        <div>
          <ATMTextField
            name="couponCode"
            required
            value={values.couponCode}
            onChange={(e) => {
              setFieldValue("couponCode", e.target.value);
            }}
            label="Coupon Code"
            onBlur={handleBlur}
          />
        </div>

        {/* Min Order Amount */}
        <div>
          <ATMTextField
            name="minOrderAmount"
            required
            value={values.minOrderAmount}
            onChange={(e) => {
              handleValidDecimalNumber(e) &&
                setFieldValue("minOrderAmount", e.target.value);
            }}
            label="Min Order Amount"
            onBlur={handleBlur}
          />
        </div>

        {/* Discount Type & Value */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
          <div>
            <ATMSelect
              name="discountType"
              options={discountOption}
              required
              value={values.discountType}
              onChange={(value) => {
                setFieldValue("discountType", value);
              }}
              label="Discount Type"
              onBlur={handleBlur}
            />
          </div>
          <div>
            <ATMTextField
              name="discountValue"
              required
              value={values.discountValue}
              onChange={(e) => {
                handleValidDecimalNumber(e) &&
                  setFieldValue("discountValue", e.target.value);
              }}
              label="Discount Value"
              onBlur={handleBlur}
            />
          </div>

          {/* Start Date */}
          <div>
            <ATMDatePicker
              name="startsDate"
              value={values.startsDate}
              required
              format="DD/MM/YYYY"
              onChange={(value) =>
                setFieldValue("startsDate", moment(value).format("YYYY-MM-DD"))
              }
              label="Start Date"
            />
          </div>

          {/* Expiry Date */}
          <div>
            <ATMDatePicker
              name="expiryDate"
              format="DD/MM/YYYY"
              value={values.expiryDate}
              onChange={(val) =>
                setFieldValue("expiryDate", moment(val).format("YYYY-MM-DD"))
              }
              label="Expiry Date"
            />
          </div>
        </div>

        {/* Max Count */}
        <div>
          <ATMTextField
            name="maxCount"
            required
            value={values.maxCount}
            onChange={(e) => {
              handleValidDecimalNumber(e) &&
                setFieldValue("maxCount", e.target.value);
            }}
            label="Max Count"
            onBlur={handleBlur}
          />
        </div>

        {/* Products */}
        <div>
          <ATMMultiSelect
            name="product"
            options={productsOption}
            value={values.product}
            isLoading={isProductLoading}
            onChange={(value) => {
              setFieldValue("product", value);
            }}
            label="Products"
            placeholder="All"
            onBlur={handleBlur}
          />
        </div>

        {/* Students */}
        <div>
          <ATMMultiSelect
            name="student"
            options={studentsOption}
            isLoading={isUserLoading}
            value={values.student}
            onChange={(value) => {
              setFieldValue("student", value);
            }}
            label="Student"
            placeholder="All"
            onBlur={handleBlur}
          />
        </div>
      </div>

      {/* Save Button */}
      <div>
        <ATMLoadingButton isLoading={isSubmitting} type="submit">
          Save
        </ATMLoadingButton>
      </div>
    </div>
  );
};

export default CouponForm;
